/**
 * Encompass milestones and webhook (configuration.encompass.list → /loans/configuration/encompass). LOP-owned.
 *
 * Bill 2026-11-03: which Encompass milestone files the post-closing task is admin configuration, per template. The Encompass Post
 * Closing watcher reads this every pass:
 *   - a loan is judged by ITS template; unknown or inactive templates, or a template with no post-closing milestone, never trigger;
 *   - the milestone must be a TRACKED one (its Log.MS.DateTime must be in the Encompass Reporting Database, or the query fails).
 * Every save asks for a reason; the API records it in loans.AdminAudit with the before and after.
 *
 * Layout follows the house editor pattern (src/modules/System/SettingsPage.tsx): a Card per table with counts in the title and
 * the filter / "+ Add" in its actions slot, a per-row Edit button, and every edit in a ModalShell carrying the change reason, the
 * saving state and the error; a dismissible toast on success.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { BoolField, SelectField, TextArea, TextField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { LoansApiError } from '../loansApi';
import { errorText, fmtCt } from '../writeups/common';
import { EncompassWebhookPanel } from './EncompassWebhookPanel';
import {
  encompassConfigApi,
  type EncompassConfig,
  type EncompassTemplate,
  type EncompassTrackedMilestone,
} from './encompassConfigApi';

const NONE = '';

interface TemplateDraft {
  isNew: boolean;
  templateId: string;
  name: string;
  generation: 'new' | 'legacy';
  active: boolean;
  milestone: string;
  notes: string;
}

interface MilestoneDraft {
  isNew: boolean;
  name: string;
  tracked: boolean;
  notes: string;
}

const blankToNull = (s: string): string | null => (s.trim() ? s.trim() : null);

/** The API's own sentence, without the code suffix (the modal is for admins, not developers). */
function modalError(e: unknown): string {
  if (e instanceof LoansApiError) return e.message ? e.message.charAt(0).toUpperCase() + e.message.slice(1) : errorText(e);
  return errorText(e);
}

function workflowLabel(generation: string): string {
  if (generation === 'new') return 'New workflow (after 10/23/2026)';
  if (generation === 'legacy') return 'Legacy';
  return generation;
}

function updatedCell(iso: string | null | undefined, by: string | null | undefined): JSX.Element {
  if (!iso && !by) return <span className="cell-dim">—</span>;
  return (
    <span className="cell-desc">
      {fmtCt(iso)}
      {by ? ` by ${by}` : ''}
    </span>
  );
}

function notesCell(notes: string | null): JSX.Element {
  return notes ? <span className="cell-desc">{notes}</span> : <span className="cell-dim">—</span>;
}

function templateToDraft(t: EncompassTemplate): TemplateDraft {
  return {
    isNew: false,
    templateId: String(t.templateId),
    name: t.name,
    generation: t.generation === 'legacy' ? 'legacy' : 'new',
    active: t.active,
    milestone: t.postClosingMilestone ?? NONE,
    notes: t.notes ?? '',
  };
}

function emptyTemplateDraft(): TemplateDraft {
  return { isNew: true, templateId: '', name: '', generation: 'new', active: true, milestone: NONE, notes: '' };
}

export function EncompassConfigPage(): JSX.Element {
  const [cfg, setCfg] = useState<EncompassConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [filter, setFilter] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const [tDraft, setTDraft] = useState<TemplateDraft | null>(null);
  const [mDraft, setMDraft] = useState<MilestoneDraft | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── Load ───────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadError(null);
      try {
        const r = await encompassConfigApi.get();
        if (!cancelled) setCfg(r);
      } catch (e) {
        if (!cancelled) setLoadError(errorText(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const usedBy = useCallback(
    (name: string): number[] =>
      (cfg?.templates ?? [])
        .filter((t) => (t.postClosingMilestone ?? '').toLowerCase() === name.toLowerCase())
        .map((t) => t.templateId),
    [cfg],
  );

  const trackedNames = useMemo(
    () => new Set((cfg?.milestones ?? []).filter((m) => m.tracked).map((m) => m.name.toLowerCase())),
    [cfg],
  );

  const filteredTemplates = useMemo(() => {
    if (!cfg) return [];
    const n = filter.trim().toLowerCase();
    if (!n) return cfg.templates;
    return cfg.templates.filter((t) =>
      `${t.templateId} ${t.name} ${workflowLabel(t.generation)} ${t.postClosingMilestone ?? ''} ${t.notes ?? ''}`.toLowerCase().includes(n),
    );
  }, [cfg, filter]);

  // ── Modals ─────────────────────────────────────────────────────────────
  const openModal = (open: () => void): void => {
    setReason('');
    setTried(false);
    setSaveError(null);
    open();
  };

  const closeModal = (): void => {
    if (saving) return;
    setTDraft(null);
    setMDraft(null);
    setSaveError(null);
  };

  const templateIdNum = tDraft ? Number(tDraft.templateId.trim()) : NaN;
  const templateProblem = ((): string | null => {
    if (!tDraft || !cfg) return null;
    if (tDraft.isNew) {
      if (!Number.isInteger(templateIdNum) || templateIdNum <= 0) return 'Enter the Encompass template ID (a whole number).';
      if (cfg.templates.some((t) => t.templateId === templateIdNum)) return `Template ${templateIdNum} is already set up.`;
      if (!tDraft.name.trim()) return 'Enter the template name as Encompass shows it.';
      return null;
    }
    const t = cfg.templates.find((x) => x.templateId === templateIdNum);
    if (
      t &&
      t.active === tDraft.active &&
      (t.postClosingMilestone ?? NONE) === tDraft.milestone &&
      (t.notes ?? '') === tDraft.notes.trim()
    ) {
      return 'Nothing has changed.';
    }
    return null;
  })();

  const milestoneProblem = ((): string | null => {
    if (!mDraft || !cfg) return null;
    const name = mDraft.name.trim();
    if (mDraft.isNew) {
      if (!name) return 'Enter the milestone name exactly as Encompass shows it.';
      if (cfg.milestones.some((m) => m.name.toLowerCase() === name.toLowerCase())) return `${name} is already in the list.`;
      return null;
    }
    const m = cfg.milestones.find((x) => x.name === mDraft.name);
    if (m && m.tracked === mDraft.tracked && (m.notes ?? '') === mDraft.notes.trim()) return 'Nothing has changed.';
    return null;
  })();

  const saveTemplate = async (): Promise<void> => {
    if (!tDraft) return;
    setTried(true);
    if (templateProblem) {
      setSaveError(templateProblem);
      return;
    }
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      const why = asciiReason(reason);
      const body = { active: tDraft.active, postClosingMilestone: tDraft.milestone || null, notes: blankToNull(tDraft.notes) };
      const label = `template ${templateIdNum} (${tDraft.name.trim()})`;
      if (tDraft.isNew) {
        await encompassConfigApi.addTemplate({ templateId: templateIdNum, name: tDraft.name.trim(), generation: tDraft.generation, ...body }, why);
        setToast(`Added ${label}.`);
      } else {
        await encompassConfigApi.saveTemplate(templateIdNum, body, why);
        setToast(`Updated ${label}.`);
      }
      setTDraft(null);
      refresh();
    } catch (e) {
      setSaveError(modalError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveMilestone = async (): Promise<void> => {
    if (!mDraft) return;
    setTried(true);
    if (milestoneProblem) {
      setSaveError(milestoneProblem);
      return;
    }
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      const name = mDraft.name.trim();
      await encompassConfigApi.saveMilestone(name, mDraft.tracked, blankToNull(mDraft.notes), asciiReason(reason));
      setToast(mDraft.isNew ? `Now tracking milestone "${name}".` : `Updated milestone "${name}".`);
      setMDraft(null);
      refresh();
    } catch (e) {
      setSaveError(modalError(e));
    } finally {
      setSaving(false);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────
  const templateColumns: ReadonlyArray<DataTableColumn<EncompassTemplate>> = [
    { header: 'Template ID', cell: (t) => <span className="cell-mono--bold">{t.templateId}</span>, sortKey: (t) => t.templateId },
    { header: 'Name', cell: (t) => t.name, sortKey: (t) => t.name },
    { header: 'Workflow', cell: (t) => workflowLabel(t.generation), sortKey: (t) => t.generation },
    {
      header: 'Active',
      cell: (t) => (t.active ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      sortKey: (t) => (t.active ? 0 : 1),
    },
    {
      header: 'Post-closing milestone',
      cell: (t) =>
        t.postClosingMilestone ? (
          <>
            {t.postClosingMilestone}
            {!trackedNames.has(t.postClosingMilestone.toLowerCase()) && (
              <>
                {' '}
                <Pill tone="warn">not tracked</Pill>
              </>
            )}
          </>
        ) : (
          <Pill tone="warn">None — never files</Pill>
        ),
      sortKey: (t) => t.postClosingMilestone ?? '',
    },
    { header: 'Notes', cell: (t) => notesCell(t.notes) },
    { header: 'Updated', cell: (t) => updatedCell(t.updatedUtc, t.updatedBy), sortKey: (t) => t.updatedUtc },
    {
      header: '',
      cell: (t) => <Button onClick={() => openModal(() => setTDraft(templateToDraft(t)))}>Edit</Button>,
    },
  ];

  const milestoneColumns: ReadonlyArray<DataTableColumn<EncompassTrackedMilestone>> = [
    { header: 'Name', cell: (m) => m.name, sortKey: (m) => m.name },
    {
      header: 'Tracked',
      cell: (m) => (m.tracked ? <Pill tone="ok">tracked</Pill> : <Pill tone="muted">not tracked</Pill>),
      sortKey: (m) => (m.tracked ? 0 : 1),
    },
    {
      header: 'Used by',
      cell: (m) => {
        const ids = usedBy(m.name);
        return ids.length ? `Template ${ids.join(', ')}` : <span className="cell-dim">—</span>;
      },
    },
    { header: 'Notes', cell: (m) => notesCell(m.notes) },
    { header: 'Updated', cell: (m) => updatedCell(m.updatedUtc, m.updatedBy), sortKey: (m) => m.updatedUtc },
    {
      header: '',
      cell: (m) => (
        <Button onClick={() => openModal(() => setMDraft({ isNew: false, name: m.name, tracked: m.tracked, notes: m.notes ?? '' }))}>
          Edit
        </Button>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <Banner tone="err">
        Could not load the Encompass configuration: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!cfg) return <Spinner label="Loading the Encompass configuration…" />;

  const noTrigger = cfg.templates.filter((t) => t.active && !t.postClosingMilestone);

  // Milestone options for the template modal: tracked ones, plus a stored milestone that is no longer tracked so the
  // modal reads truthfully.
  const milestoneOptions = [
    { value: NONE, label: '(none) — loans on this template never file a post-closing task' },
    ...cfg.milestones.filter((m) => m.tracked).map((m) => ({ value: m.name, label: m.name })),
  ];
  if (tDraft?.milestone && !milestoneOptions.some((o) => o.value === tDraft.milestone)) {
    milestoneOptions.push({ value: tDraft.milestone, label: `${tDraft.milestone} (no longer tracked)` });
  }

  const mInUse = mDraft && !mDraft.isNew ? usedBy(mDraft.name) : [];

  return (
    <div>
      <h2>Encompass milestones and webhook</h2>
      <p className="page-intro">
        Choose which Encompass milestone files the post-closing task, one template at a time. Each loan is judged by its own
        template; a template with no post-closing milestone, or one that is switched off, never files a task. Loans started after
        10/23/2026 run on the new-workflow templates; older loans stay on the legacy templates until they finish.
      </p>
      <p className="form-field__hint">
        The post-closing check is watching for:{' '}
        {cfg.triggerMilestones.length ? cfg.triggerMilestones.join(', ') : 'nothing — no template can file a task right now'}.
      </p>

      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      {noTrigger.length > 0 && (
        <Banner tone="warn">
          {noTrigger.length === 1 ? 'This active template has' : 'These active templates have'} no post-closing milestone, so their
          loans never file a post-closing task: {noTrigger.map((t) => `${t.templateId} ${t.name}`).join('; ')}. Edit the template
          to choose one.
        </Banner>
      )}

      <Card
        title={
          filter.trim()
            ? `Encompass milestone templates — ${filteredTemplates.length} of ${cfg.templates.length}`
            : `Encompass milestone templates — ${cfg.templates.length}`
        }
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField label="" value={filter} onChange={setFilter} placeholder="Filter by ID, name, milestone or notes…" />
            </div>
            <Button variant="primary" onClick={() => openModal(() => setTDraft(emptyTemplateDraft()))}>
              + Add template
            </Button>
          </div>
        }
      >
        <DataTable<EncompassTemplate>
          columns={templateColumns}
          rows={filteredTemplates}
          rowKey={(t) => String(t.templateId)}
          emptyState={
            <p className="empty">{cfg.templates.length ? 'No templates match the filter.' : 'No templates are set up yet.'}</p>
          }
        />
      </Card>

      <Card
        title={`Tracked milestones — ${cfg.milestones.length}`}
        actions={
          <div className="card-actions-row">
            <Button variant="primary" onClick={() => openModal(() => setMDraft({ isNew: true, name: '', tracked: true, notes: '' }))}>
              + Track a milestone
            </Button>
          </div>
        }
      >
        <p className="form-field__hint">
          Only a tracked milestone can file the post-closing task. A milestone a template uses cannot be untracked until that
          template is changed.
        </p>
        <DataTable<EncompassTrackedMilestone>
          columns={milestoneColumns}
          rows={cfg.milestones}
          rowKey={(m) => m.name}
          emptyState={<p className="empty">No milestones are tracked yet.</p>}
        />
      </Card>

      <EncompassWebhookPanel />

      {/* ── Template modal ─────────────────────────────────────────────── */}
      <ModalShell
        open={tDraft !== null}
        title={tDraft?.isNew ? 'Add template' : `Edit template ${tDraft?.templateId ?? ''}`}
        onClose={closeModal}
        width={620}
        actions={
          tDraft && (
            <>
              <Button onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void saveTemplate()} disabled={saving}>
                {saving ? 'Saving…' : tDraft.isNew ? 'Add template' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {tDraft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <TextField
              label="Template ID"
              value={tDraft.templateId}
              onChange={(v) => setTDraft({ ...tDraft, templateId: v })}
              disabled={!tDraft.isNew}
              monospace
              hint={tDraft.isNew ? 'The ID Encompass gives this milestone template.' : 'The template ID cannot be changed.'}
            />
            <TextField
              label="Name"
              value={tDraft.name}
              onChange={(v) => setTDraft({ ...tDraft, name: v })}
              disabled={!tDraft.isNew}
              maxLength={100}
              hint={tDraft.isNew ? 'As Encompass shows it.' : undefined}
            />
            <SelectField
              label="Workflow"
              value={tDraft.generation}
              onChange={(v) => setTDraft({ ...tDraft, generation: v === 'legacy' ? 'legacy' : 'new' })}
              disabled={!tDraft.isNew}
              options={[
                { value: 'new', label: workflowLabel('new') },
                { value: 'legacy', label: workflowLabel('legacy') },
              ]}
            />
            <SelectField
              label="Post-closing milestone"
              value={tDraft.milestone}
              onChange={(v) => setTDraft({ ...tDraft, milestone: v })}
              options={milestoneOptions}
              hint="When a loan on this template completes this milestone, its post-closing task is filed. Only tracked milestones are offered."
            />
            <BoolField
              label="Active"
              value={tDraft.active}
              onChange={(v) => setTDraft({ ...tDraft, active: v })}
              hint="An inactive template never files a post-closing task."
            />
            <TextArea
              label="Notes"
              value={tDraft.notes}
              onChange={(v) => setTDraft({ ...tDraft, notes: v })}
              rows={2}
            />
            <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
          </div>
        )}
      </ModalShell>

      {/* ── Milestone modal ────────────────────────────────────────────── */}
      <ModalShell
        open={mDraft !== null}
        title={mDraft?.isNew ? 'Track a milestone' : `Edit milestone ${mDraft?.name ?? ''}`}
        onClose={closeModal}
        width={620}
        actions={
          mDraft && (
            <>
              <Button onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void saveMilestone()} disabled={saving}>
                {saving ? 'Saving…' : mDraft.isNew ? 'Track milestone' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {mDraft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            {mDraft.isNew && (
              <TextField
                label="Milestone name"
                value={mDraft.name}
                onChange={(v) => setMDraft({ ...mDraft, name: v })}
                maxLength={64}
                hint="Exactly as Encompass shows it. It must already be in the Encompass Reporting Database."
              />
            )}
            <BoolField
              label="Tracked"
              value={mDraft.tracked}
              onChange={(v) => setMDraft({ ...mDraft, tracked: v })}
              hint={
                mInUse.length
                  ? `Used by template ${mInUse.join(', ')} — change those templates before untracking it.`
                  : 'Only a tracked milestone can be chosen as a template’s post-closing milestone.'
              }
            />
            <TextArea
              label="Notes"
              value={mDraft.notes}
              onChange={(v) => setMDraft({ ...mDraft, notes: v })}
              rows={2}
            />
            <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
          </div>
        )}
      </ModalShell>
    </div>
  );
}
